import React, { useState, useEffect } from 'react';
import { X, Plus, Trash2, Info } from 'lucide-react';
import api from '../../utils/api';

const CRM_FIELDS = [
  { value: 'customer.name', label: 'שם לקוח' },
  { value: 'customer.phone', label: 'טלפון ראשי' },
  { value: 'customer.email', label: 'אימייל' },
  { value: 'customer.city', label: 'עיר' },
  { value: 'customer.address', label: 'כתובת' },
  { value: 'customer.status', label: 'סטטוס לקוח' },
  { value: 'customer.notes', label: 'הערות לקוח' },
  { value: 'customer.created_at', label: 'תאריך הצטרפות' },
  { value: 'order.number', label: 'מספר הזמנה' },
  { value: 'order.status', label: 'סטטוס הזמנה' },
  { value: 'order.notes', label: 'הערות הזמנה' },
  { value: 'order.total', label: 'סכום הזמנה' },
  { value: 'order.created_at', label: 'תאריך הזמנה' },
  { value: 'date.today', label: 'תאריך היום (קצר)' },
  { value: 'date.today_full', label: 'תאריך היום (מלא)' },
];

export default function TemplateModal({ template, onClose, onSaved }) {
  const [form, setForm] = useState({
    name: '',
    description: '',
    html_content: '',
    is_default_order: false,
    variable_mappings: {},
  });
  const [newVarKey, setNewVarKey] = useState('');
  const [newVarField, setNewVarField] = useState(CRM_FIELDS[0].value);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [showHelp, setShowHelp] = useState(false);

  useEffect(() => {
    if (template) {
      setForm({
        name: template.name || '',
        description: template.description || '',
        html_content: template.html_content || '',
        is_default_order: template.is_default_order || false,
        variable_mappings: template.variable_mappings || {},
      });
    }
  }, [template]);

  function set(key, val) {
    setForm((f) => ({ ...f, [key]: val }));
  }

  function addMapping() {
    if (!newVarKey.trim()) return;
    const key = newVarKey.startsWith('{{') ? newVarKey : `{{${newVarKey}}}`;
    setForm((f) => ({
      ...f,
      variable_mappings: { ...f.variable_mappings, [key]: newVarField },
    }));
    setNewVarKey('');
  }

  function removeMapping(key) {
    setForm((f) => {
      const mappings = { ...f.variable_mappings };
      delete mappings[key];
      return { ...f, variable_mappings: mappings };
    });
  }

  async function handleSubmit(e) {
    e.preventDefault();
    if (!form.name || !form.html_content) {
      setError('שם ותוכן HTML נדרשים');
      return;
    }
    setSaving(true);
    setError('');
    try {
      if (template) {
        await api.put(`/documents/templates/${template.id}`, form);
      } else {
        await api.post('/documents/templates', form);
      }
      onSaved();
    } catch (err) {
      setError(err.response?.data?.error || 'שגיאה בשמירה');
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" dir="rtl">
      <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-xl w-full max-w-3xl max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-5 border-b border-gray-200 dark:border-gray-700">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
            {template ? 'עריכת תבנית' : 'תבנית חדשה'}
          </h2>
          <button onClick={onClose} className="p-1.5 rounded-lg text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700">
            <X size={18} />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-5 space-y-5">
          {error && (
            <div className="p-3 rounded-lg bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 text-red-700 dark:text-red-400 text-sm">
              {error}
            </div>
          )}

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="text-sm font-medium text-gray-700 dark:text-gray-300 block mb-1">שם תבנית *</label>
              <input value={form.name} onChange={(e) => set('name', e.target.value)} required
                className="w-full px-3 py-2 text-sm rounded-lg border border-gray-200 dark:border-gray-600 bg-gray-50 dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary-500" />
            </div>
            <div>
              <label className="text-sm font-medium text-gray-700 dark:text-gray-300 block mb-1">תיאור</label>
              <input value={form.description} onChange={(e) => set('description', e.target.value)}
                className="w-full px-3 py-2 text-sm rounded-lg border border-gray-200 dark:border-gray-600 bg-gray-50 dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary-500" />
            </div>
          </div>

          <label className="flex items-center gap-2 cursor-pointer">
            <input type="checkbox" checked={form.is_default_order} onChange={(e) => set('is_default_order', e.target.checked)}
              className="rounded border-gray-300 text-primary-600 focus:ring-primary-500" />
            <span className="text-sm text-gray-700 dark:text-gray-300">תבנית ברירת מחדל להזמנות</span>
          </label>

          {/* HTML Content */}
          <div>
            <div className="flex items-center justify-between mb-1">
              <label className="text-sm font-medium text-gray-700 dark:text-gray-300">תוכן HTML *</label>
              <button type="button" onClick={() => setShowHelp(!showHelp)}
                className="flex items-center gap-1 text-xs text-gray-500 hover:text-primary-600">
                <Info size={12} /> עזרה עם משתנים
              </button>
            </div>
            {showHelp && (
              <div className="mb-2 p-3 rounded-lg bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 text-xs text-blue-700 dark:text-blue-300">
                <p className="font-medium mb-1">שימוש במשתנים ב-HTML:</p>
                <p>הוסף משתנים בפורמט <code className="bg-blue-100 dark:bg-blue-900 px-1 rounded">{`{{שם_משתנה}}`}</code> ב-HTML שלך.</p>
                <p className="mt-1">לאחר מכן, קשר כל משתנה לשדה CRM בטבלת המשתנים למטה.</p>
                <p className="mt-1">לדוגמה: <code className="bg-blue-100 dark:bg-blue-900 px-1 rounded">{`{{שם_לקוח}}`}</code> → שם לקוח</p>
              </div>
            )}
            <textarea
              value={form.html_content}
              onChange={(e) => set('html_content', e.target.value)}
              rows={12}
              required
              placeholder="<!DOCTYPE html><html>...</html>"
              className="w-full px-3 py-2 text-sm rounded-lg border border-gray-200 dark:border-gray-600 bg-gray-50 dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary-500 font-mono resize-none"
              dir="ltr"
            />
          </div>

          {/* Variable Mappings */}
          <div>
            <label className="text-sm font-medium text-gray-700 dark:text-gray-300 block mb-2">
              מיפוי משתנים ({Object.keys(form.variable_mappings).length})
            </label>

            {Object.entries(form.variable_mappings).length > 0 && (
              <div className="space-y-1.5 mb-3">
                {Object.entries(form.variable_mappings).map(([key, field]) => (
                  <div key={key} className="flex items-center gap-2 text-sm">
                    <code className="px-2 py-1 rounded bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 text-xs font-mono flex-1">
                      {key}
                    </code>
                    <span className="text-gray-400">→</span>
                    <span className="text-gray-600 dark:text-gray-400 flex-1">
                      {CRM_FIELDS.find((f) => f.value === field)?.label || field}
                    </span>
                    <button type="button" onClick={() => removeMapping(key)}
                      className="p-1 text-gray-400 hover:text-red-500">
                      <Trash2 size={13} />
                    </button>
                  </div>
                ))}
              </div>
            )}

            {/* Add mapping */}
            <div className="flex gap-2 items-center">
              <input
                value={newVarKey}
                onChange={(e) => setNewVarKey(e.target.value)}
                placeholder="{{שם_משתנה}}"
                className="flex-1 px-3 py-2 text-sm rounded-lg border border-gray-200 dark:border-gray-600 bg-gray-50 dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary-500 font-mono"
                dir="ltr"
                onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), addMapping())}
              />
              <select value={newVarField} onChange={(e) => setNewVarField(e.target.value)}
                className="flex-1 px-3 py-2 text-sm rounded-lg border border-gray-200 dark:border-gray-600 bg-gray-50 dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary-500">
                {CRM_FIELDS.map((f) => (
                  <option key={f.value} value={f.value}>{f.label}</option>
                ))}
              </select>
              <button type="button" onClick={addMapping}
                className="px-3 py-2 rounded-lg bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600 text-sm transition-colors">
                <Plus size={14} />
              </button>
            </div>
          </div>

          <div className="flex gap-3 pt-2">
            <button type="button" onClick={onClose}
              className="flex-1 px-4 py-2.5 rounded-lg border border-gray-200 dark:border-gray-600 text-sm font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700">
              ביטול
            </button>
            <button type="submit" disabled={saving}
              className="flex-1 px-4 py-2.5 rounded-lg bg-primary-600 hover:bg-primary-700 text-white text-sm font-medium disabled:opacity-60">
              {saving ? 'שומר...' : (template ? 'שמור שינויים' : 'צור תבנית')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
